import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { AiOutlineClose, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import { IoIosCloseCircle } from "react-icons/io";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SummaryApi from '../common';
import NotInterestedModal from '../components/NotInterestedModal';
import Select from 'react-select';

// ─────────────────────────────────────────────────────────────
// Inline Advanced Search Modal (was NotinterestedAdvancedSearchModal)
// ─────────────────────────────────────────────────────────────
const AdvancedSearchModal = ({
  isOpen,
  onRequestClose,
  filteredStudents,
  searchDistrict,
  setSearchDistrict,
  searchCity,
  setSearchCity,
  searchCollege,
  setSearchCollege,
  setFilteredStudents,
  studentsData,
}) => {
  const collegeOptions = useMemo(() => {
    if (!searchCity) return [];

    return Array.from(new Set(
      filteredStudents
        .filter(student =>
          searchCity && student.city?.toLowerCase() === searchCity.toLowerCase()
        )
        .map(student => student.clg_name)
    ))
      .filter(Boolean)
      .map(college => ({
        value: college,
        label: college
      }));
  }, [filteredStudents, searchCity]);

const handleApplyFilters = () => {
  onRequestClose(); // just close — useEffect will auto-filter via searchDistrict/searchCity/searchCollege state
};
  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Search Filters"
      className="mx-auto my-10 mt-[100px] max-w-md bg-white rounded-lg shadow-lg"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50"
    >
      <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Search Filters</h2>
          <button onClick={onRequestClose}>
            <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* District Dropdown */}
        <div className="flex flex-col">
          <label htmlFor="district-filter" className="mb-1 font-medium">
            District
          </label>
          <select
            id="district-filter"
            className="p-2 border rounded"
            value={searchDistrict}
            onChange={(e) => {
              setSearchDistrict(e.target.value);
              setSearchCity('');
              setSearchCollege([]);
            }}
          >
            <option value="">Select District</option>
            {Array.from(new Set(filteredStudents.map(student => student.district)))
              .filter(Boolean)
              .map(district => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
          </select>
        </div>

        {/* City Dropdown */}
        <div className="flex flex-col">
          <label htmlFor="city-filter" className="mb-1 font-medium">
            City
          </label>
          <select
            id="city-filter"
            className="p-2 border rounded"
            value={searchCity}
            onChange={(e) => {
              setSearchCity(e.target.value);
              setSearchCollege([]);
            }}
            disabled={!searchDistrict}
          >
            <option value="">
              {searchDistrict ? "Select City" : "First select District"}
            </option>
            {Array.from(new Set(
              filteredStudents
                .filter(student =>
                  searchDistrict && student.district?.toLowerCase() === searchDistrict.toLowerCase()
                )
                .map(student => student.city)
            ))
              .filter(Boolean)
              .map(city => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
          </select>
        </div>

        {/* College Multi-Select */}
        <div className="flex flex-col">
          <label htmlFor="college-filter" className="mb-1 font-medium">
            College Name
          </label>
          <Select
            isMulti
            options={collegeOptions}
            value={collegeOptions.filter(option =>
              Array.isArray(searchCollege) && searchCollege.includes(option.value)
            )}
            onChange={(selectedOptions) =>
              setSearchCollege(selectedOptions.map(option => option.value))
            }
            isDisabled={!searchCity}
            placeholder={searchCity ? "Select College(s)" : "First select City"}
          />
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={() => {
              setSearchDistrict('');
              setSearchCity('');
              setSearchCollege([]);
              setFilteredStudents(studentsData);
            }}
            className="px-4 py-2 mr-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Clear
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────
// Main NotInterestedStudents Component
// ─────────────────────────────────────────────────────────────
const NotInterestedStudents = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [enrollmentCode, setEnrollmentCode] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [dropdownValue, setDropdownValue] = useState('');
  const [minEnrolledDate, setMinEnrolledDate] = useState(null);
  const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);
  
  const [formData, setFormData] = useState({
    student_name: '',
    gender: 'Male',
    father_name: '',
    mobile_number1: '',
    mobile_number2: '',
    email: '',
    aadhar_number: '',
    state: '',
    district: '',
    city: '',
    address: '',
    pincode: '',
    course: '',
    clg_name: '',
    program_type: '',
    ug_pg_course: '',
    call_back_reason: '',
    call_duration: '00:00:00.000000',
    other_reasons: '',
    is_interested: '',
    call_status: '',
    remarks: '',
    prev_call_back_reason: ''
  });
  const [prevInterest, setPrevInterest] = useState('');
  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchDistrict, setSearchDistrict] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchCollege, setSearchCollege] = useState([]);
  const [canOnlySelectHigherInterest, setCanOnlySelectHigherInterest] = useState(false);
  const tableRef = useRef(null);
  const [latestFollowUpId, setLatestFollowUpId] = useState(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  const districts = [
    'Alluri Sitharama Raju', 'Anakapalli', 'Ananthapuram', 'Annamayya', 'Bapatla', 'CHITTOOR',
    'EAST GODAVARI', 'Eluru', 'Guntur', 'Kakinada', 'Konaseema', 'Krishna', 'Kurnool',
    'Manyam', 'Nandyal', 'NTR', 'Palnadu', 'Prakasam', 'Srikakulam', 'SPS Nellore',
    'Sri Satyasai', 'Srikakulam', 'Tirupathi', 'Vizianagaram', 'Visakhapatnam',
    'West Godavari', 'YSR Kadapa'
  ];

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
      const memberType = encodedMemberType ? atob(encodedMemberType) : null;
      setMemberType(memberType);

      let response;
      if (memberType === 'Admin' || memberType === 'Manager') {
        response = await fetch(SummaryApi.getAllNotInterestedStudents.url, {
          method: SummaryApi.getAllNotInterestedStudents.method,
          headers: headers,
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
        if (!encodedEmployeeId) throw new Error('Employee ID not found in session storage');
        const decodedEmployeeId = atob(encodedEmployeeId);
        response = await fetch(SummaryApi.notInterestedStudents.url, {
          method: SummaryApi.notInterestedStudents.method,
          headers: headers,
          body: JSON.stringify({ employee_id: decodedEmployeeId }),
        });
      }

      if (!response.ok) throw new Error('Failed to fetch data from the endpoint');
      const data = await response.json();
      const students = data?.data || [];
      
      const updatedStudentsData = students.map((student, index) => ({
        ...student,
        sno: index + 1,
      }));

      setStudentsData(updatedStudentsData || []);
      
      let latestIndex = -1;
      let latestDate = null;

      updatedStudentsData.forEach((student, index) => {
        const fud = student.follow_up_date?.trim();
        if (!fud || fud === "0000-00-00 00:00:00" || fud === "N/A") return;
        const dateObj = new Date(fud);
        if (isNaN(dateObj.getTime())) return;
        if (!latestDate || dateObj > latestDate) {
          latestDate = dateObj;
          latestIndex = index;
        }
      });

      if (latestIndex !== -1) {
        setLatestFollowUpId(updatedStudentsData[latestIndex].student_id);
        setShouldAutoScroll(true);
      } else {
        setLatestFollowUpId(null);
        setShouldAutoScroll(false);
      }

    } catch (error) {
      toast.error(`Error fetching students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (studentsData.length > 0) {
      filterStudents(selectedFilter, searchQuery, selectedRange);
    }
  }, [studentsData, selectedFilter, searchQuery, selectedRange, searchDistrict, searchCity, searchCollege]);

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    filterStudents(filter, searchQuery, selectedRange);
  };

  const filterStudents = (filter, search, range, district = searchDistrict, city = searchCity, college = searchCollege, data = studentsData) => {
    let filtered = data;
    
    const timeZone = 'Asia/Kolkata';
    const formatToKolkata = (date) => {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    };

    if (filter !== 'All') {
      if (filter === "JEE Mains") {
        filtered = filtered.filter((student) => student.is_jee === "Jee Mains Results");
      } else if (filter === "JEE Mains2") {
        filtered = filtered.filter((student) => student.is_jee2 === "Jee Mains-2 Results");
      } else if (filter === "Not JEE Mains") {
        filtered = filtered.filter((student) => 
          student.is_jee !== "Jee Mains Results" && 
          student.is_jee2 !== "Jee Mains-2 Results"
        );
      }
    }

    if (district) {
      filtered = filtered.filter((student) =>
        student.district?.toLowerCase().includes(district.toLowerCase())
      );
    }
    if (city) {
      filtered = filtered.filter((student) =>
        student.city?.toLowerCase().includes(city.toLowerCase())
      );
    }
    if (Array.isArray(college) && college.length > 0) {
      filtered = filtered.filter((student) =>
        college.some(c =>
          student.clg_name?.toLowerCase() === c.toLowerCase()
        )
      );
    }

    const convertToKolkataDateString = (date) => formatToKolkata(new Date(date));

    if (range === 'today') {
      const today = new Date();
      const todayFormatted = formatToKolkata(today);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === todayFormatted;
      });
    } else if (range === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = formatToKolkata(yesterday);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === yesterdayFormatted;
      });
    } else if (range === 'thisWeek') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfWeek && studentDate <= endOfWeek;
      });
    } else if (range === 'lastWeek') {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfLastWeek && studentDate <= endOfLastWeek;
      });
    } else if (range === 'custom') {
      if (!fromDate || !toDate) return;
      const fromFormatted = new Date(fromDate);
      fromFormatted.setHours(0, 0, 0, 0);
      const toFormatted = new Date(toDate);
      toFormatted.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= fromFormatted && studentDate <= toFormatted;
      });
    }

    if (['Admin', 'Manager'].includes(memberType) && search) {
      filtered = filtered.filter((student) =>
        student.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    } else if (search) {
      filtered = filtered.filter((student) =>
        student.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredStudents(filtered);
  };

  useEffect(() => {
    if (studentsData?.length) {
      const dates = studentsData.map(student => new Date(student.created_date));
      const minDate = new Date(Math.min(...dates));
      setMinEnrolledDate(minDate);
      setMaxEnrolledDate(new Date());
    }
  }, [studentsData]);

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);
    setIsCustomRange(range === 'custom');
    if (range !== 'custom') {
      setFromDate(null);
      setToDate(null);
      filterStudents(selectedFilter, searchQuery, range);
    } else {
      setFilteredStudents([]);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      filterStudents(selectedFilter, searchQuery, 'custom');
    }
  }, [fromDate, toDate]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    filterStudents(selectedFilter, e.target.value, selectedRange);
  };

  const ugCourses = ['CSE', 'CSE(AI & ML)', 'CSE(AI & DS)', 'AERONAUTICAL ENGINERRING', 'ECE'];
  const pgCourses = [];
  const courses = formData.program_type === 'UG' ? ugCourses : formData.program_type === 'PG' ? pgCourses : [];

  const openModal = (student = null, mode = 'view') => {
    const sanitizeStudentData = (data) => {
      const sanitizedData = { ...data };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === "N/A" || sanitizedData[key] === "n/a") {
          sanitizedData[key] = "";
        }
      });
      return sanitizedData;
    };

    const reasonsForOthers = [
      "Wrong Number", "Not Reachable", "Number Not Working", "Call Declined", "Busy",
      "Switched Off", "Incoming Calls Stopped"
    ];

    const payload = {
      employee_id: student?.employee_id,
      student_name: student?.student_name,
      gender: student?.gender,
      father_name: student?.father_name,
      mobile_number1: student?.mobile_number1,
      mobile_number2: student?.mobile_number2 || '',
      email: student?.email || '',
      aadhar_number: student?.aadhar_number || '',
      state: student?.state,
      district: student?.district,
      city: student?.city,
      address: student?.address,
      pincode: student?.pincode || '',
      course: student?.course,
      clg_name: student?.clg_name,
      inter_percentage: student?.inter_percentage || '',
      program_type: student?.program_type,
      ug_pg_course: student?.ug_pg_course,
      call_status: student?.call_status || '',
      call_duration: student?.call_duration || '00:00:00.000000',
      remarks: student?.remarks || '',
      is_interested: reasonsForOthers.includes(student?.is_interested) ? "Others" : student?.is_interested,
      other_reasons: reasonsForOthers.includes(student?.is_interested) ? student?.is_interested : '',
      call_back_reason: student?.call_back_reason,
      prev_call_back_reason: student?.call_back_reason,
      is_jee: student?.is_jee,
      is_jee2: student?.is_jee2,
    };

    const sanitizedStudent = sanitizeStudentData(payload);

    setPrevInterest(sanitizedStudent.is_interested || 'N/A');

    if (mode === 'edit' && sanitizedStudent.is_interested === 'Not Interested') {
      setCanOnlySelectHigherInterest(true);
    } else {
      setCanOnlySelectHigherInterest(false);
    }

    const [timePart = '00:00:00'] = sanitizedStudent.call_duration.split('.');
    const [hours, minutes, seconds] = timePart.split(':');

    setFormData({
      ...sanitizedStudent,
      hours: hours?.padStart(2, '0') || '00',
      minutes: minutes?.padStart(2, '0') || '00',
      seconds: seconds?.padStart(2, '0') || '00',
    });

    if (mode === 'edit') {
      setIsUpdate(true);
      setIsUpload(false);
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(course => course.trim())
        : [];
      setSelectedCourses(coursesArray);
    } else if (mode === 'add') {
      setFormData({
        student_name: '', gender: 'Male', father_name: '', mobile_number1: '', mobile_number2: '',
        email: '', aadhar_number: '', state: '', district: '', city: '', address: '', pincode: '',
        course: '', clg_name: '', remarks: '', call_status: '', call_duration: '00:00:00.000000',
        program_type: '', ug_pg_course: '', is_interested: '', inter_percentage: '', is_jee: '', is_jee2: '',
        hours: '00', minutes: '00', seconds: '00', other_reasons: '', call_back_reason: ''
      });
      setIsUpdate(false);
      setIsUpload(true);
    } else {
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(course => course.trim())
        : [];
      setSelectedCourses(coursesArray);
    }

    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const handleAllClick = () => {
    setSelectedFilter('All');
    setSearchQuery('');
    setSelectedRange('');
    setFromDate(null);
    setToDate(null);
    setIsCustomRange(false);
    setSearchDistrict('');
    setSearchCity('');
    setSearchCollege([]);
    setFilteredStudents(studentsData);

    const validFollowUps = studentsData.filter(
      s =>
        s.follow_up_date &&
        s.follow_up_date !== "0000-00-00 00:00:00" &&
        s.follow_up_date !== "N/A"
    );

    if (validFollowUps.length > 0) {
      const latest = validFollowUps.reduce((prev, curr) =>
        new Date(curr.follow_up_date) > new Date(prev.follow_up_date)
          ? curr
          : prev
      );
      setLatestFollowUpId(latest.student_id);
      setShouldAutoScroll(true);
    } else {
      setLatestFollowUpId(null);
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
    setIsUpdate(false);
    setIsUpload(false);
    setSelectedCourses([]);
    setDropdownValue('');
    setCanOnlySelectHigherInterest(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'call_status' && (value === 'Not Answered' || value === '')) {
      setFormData((prev) => ({
        ...prev,
        is_interested: '',
        [name]: value,
      }));
    } else if (name === 'is_interested') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(value !== 'Call Back' && { call_back_reason: '' }),
        ...(value !== 'Others' && { other_reasons: '' }),
      }));
    } else if (name === 'program_type' && (value === 'PG' || value === '')) {
      setSelectedCourses([]);
      setDropdownValue('');
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSelectChange = (e) => {
    const selectedCourse = e.target.value;
    if (selectedCourse && !selectedCourses.includes(selectedCourse)) {
      setSelectedCourses((prev) => [...prev, selectedCourse]);
    }
    setDropdownValue('');
  };

  const handleRemoveTag = (course) => {
    setSelectedCourses((prev) => prev.filter(c => c !== course));
  };

  useEffect(() => {
    if (formData.call_duration) {
      const [timePart] = formData.call_duration.split('.');
      const [hours, minutes, seconds] = timePart.split(':').map(part => part.padStart(2, '0'));
      setFormData((prevData) => ({
        ...prevData,
        hours,
        minutes,
        seconds,
      }));
    }
  }, [formData.call_duration]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
      if (!encodedEmployeeId) throw new Error('Employee ID not found in session storage');
      const decodedEmployeeId = atob(encodedEmployeeId);

      const apiEndpoint = isUpdate ? SummaryApi.updateStudent.url : SummaryApi.uploadStudent.url;
      const method = isUpdate ? SummaryApi.updateStudent.method : SummaryApi.uploadStudent.method;

      const payloadData = {
        employee_student_id: formData.employee_student_id,
        student_name: formData.student_name,
        last_name: '',
        gender: formData.gender,
        father_name: formData.father_name,
        mobile_number1: formData.mobile_number1,
        mobile_number2: formData.mobile_number2 || '',
        email: formData.email || '',
        aadhar_number: formData.aadhar_number || '',
        state: formData.state,
        district: formData.district,
        city: formData.city,
        address: formData.address,
        pincode: formData.pincode || '',
        course: formData.course,
        clg_name: formData.clg_name,
        inter_percentage: formData.inter_percentage || '',
        program_type: formData.program_type,
        ug_pg_course: selectedCourses.join(', '),
        call_status: formData.call_status || '',
        call_duration: formData.call_duration || '00:00:00.000000',
        remarks: formData.remarks || '',
        is_interested: formData.is_interested === 'Others' ? formData.other_reasons : formData.is_interested,
        prevs_is_interested: formData.is_interested === 'Not Interested' 
          ? 'Not Interested' 
          : (isUpdate ? prevInterest : ''),
        is_jee: (formData.is_jee === "Jee Mains Results")
          ? "Jee Mains Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains Results" ? "Jee Mains Results" : "")
            : "",
        is_jee2: (formData.is_jee2 === "Jee Mains-2 Results")
          ? "Jee Mains-2 Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains-2 Results" ? "Jee Mains-2 Results" : "")
            : "",
        call_back_reason: formData.call_back_reason || '',
      };

      const payload = isUpdate
        ? { ...payloadData, student_id: selectedStudent.student_id, employee_id: decodedEmployeeId }
        : { ...payloadData, employee_id: decodedEmployeeId };

      const response = await fetch(apiEndpoint, {
        method: method,
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();

      if (result.status) {
        toast.success(isUpdate ? 'Student updated successfully!' : 'Student added successfully!');
        if (!isUpdate) {
          setEnrollmentCode(result.ENR_CODE);
        }
        closeModal();
        await fetchStudents();
        setShouldAutoScroll(true);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const downloadExcelReport = () => {
    const requiredFields = [
      'enrollment_code', 'student_name', 'email', 'mobile_number1', 'mobile_number2',
      'aadhar_number', 'state', 'district', 'city', 'pincode', 'course', 'clg_name',
      'created_date', 'call_status', 'call_duration', 'remarks', 'employee_name'
    ];
    const filteredData = studentsData.map(student => {
      const selectedData = {};
      requiredFields.forEach(field => {
        selectedData[field] = student[field];
      });
      return selectedData;
    });

    const headersMap = {
      enrollment_code: 'Enrollment Code',
      student_name: 'Student Name',
      email: 'Email',
      mobile_number1: 'Mobile Number 1',
      mobile_number2: 'Mobile Number 2',
      aadhar_number: 'Aadhar Number',
      state: 'State',
      district: 'District',
      city: 'City',
      address: 'Address',
      pincode: 'Pincode',
      course: 'Course',
      clg_name: 'College',
      created_date: 'Enrolled Date',
      call_status: 'Call Status',
      call_duration: 'Call Duration',
      remarks: 'Remarks',
      employee_name: 'Employee Name'
    };

    const formattedData = filteredData.map(student => {
      return Object.fromEntries(
        Object.entries(student).map(([key, value]) => [headersMap[key] || key, value])
      );
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, 'not_interested_students_report.xlsx');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "NA";
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'SNO',
        Cell: ({ row }) => row.index + 1,
      },
      ...(['Admin', 'Manager'].includes(memberType) ? [
        { Header: 'Employee Name', accessor: 'employee_name' },
      ] : []),
      { Header: 'Student Name', accessor: 'student_name' },
      { Header: 'District', accessor: 'district' },
      { Header: 'City', accessor: 'city' },
      { Header: 'College Name', accessor: 'clg_name' },
      { Header: 'Address', accessor: 'address' },
      { Header: 'Mobile Number', accessor: 'mobile_number1' },
      {
        Header: 'Call Duration (HH:MM:SS)',
        accessor: 'call_duration',
        Cell: ({ value }) => {
          if (value === "n/a") return value;
          if (value.includes(':')) {
            const [timePart] = value.split('.');
            const [hours, minutes, seconds] = timePart.split(':');
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
          }
          return value;
        },
      },
      { Header: 'Course', accessor: 'course' },
      {
        Header: 'Enrolled Date',
        accessor: 'enroll_date',
        Cell: ({ row }) => {
          const { created_date } = row.original;
          return <span>{formatDate(created_date)}</span>;
        },
      },
      {
        Header: 'Follow Up Date',
        accessor: 'follow_up_date',
        Cell: ({ row }) => {
          const { follow_up_date } = row.original;
          return <span>{formatDate(follow_up_date)}</span>;
        },
      },
      {
        Header: 'Previous Status',
        accessor: 'prevs_is_interested',
        Cell: ({ value }) => {
          if (!value) value = 'NA';
          let colorClass = 'text-gray-600';
          if (value === 'Most Interested') colorClass = 'text-green-600';
          else if (value === 'Interested') colorClass = 'text-yellow-500';
          else if (value === 'Not Interested') colorClass = 'text-red-500';
          return <span className={`font-semibold ${colorClass}`}>{value}</span>;
        },
      },
      {
        Header: 'Current Status',
        accessor: 'is_interested',
        Cell: ({ value }) => {
          let colorClass = 'text-gray-600';
          if (value === 'Most Interested') colorClass = 'text-green-600';
          else if (value === 'Interested') colorClass = 'text-yellow-500';
          else if (value === 'Not Interested') colorClass = 'text-red-500';
          return <span className={`font-semibold ${colorClass}`}>{value}</span>;
        },
      },
      { Header: 'Jee Mains-1', accessor: 'is_jee' },
      { Header: 'Jee Mains-2', accessor: 'is_jee2' },
      ...(['Employee', 'Consultant'].includes(memberType) ? [{
        Header: 'Edit',
        accessor: 'edit',
        Cell: ({ row }) => (
          <button
            className="bg-green-600 text-white px-3 py-2 rounded-md transition-transform transform hover:scale-105"
            onClick={() => openModal(row.original, 'edit')}
          >
            <AiOutlineEdit className="inline-block mr-1" /> Edit
          </button>
        ),
      }] : []),
      ...(['Admin'].includes(memberType) ? [{
        Header: 'Edit',
        accessor: 'edit',
        Cell: ({ row }) => (
          <button
            className="bg-green-600 text-white px-3 py-2 rounded-md transition-transform transform hover:scale-105"
            onClick={() => openModal(row.original, 'edit')}
          >
            <AiOutlineEdit className="inline-block mr-1" /> Edit
          </button>
        ),
      }] : []),
      {
        Header: 'View More',
        accessor: 'view',
        Cell: ({ row }) => (
          <button
            className="bg-yellow-600 text-white px-3 py-2 rounded-md transition-transform transform hover:scale-105"
            onClick={() => openModal(row.original, 'view')}
          >
            <AiOutlineEye className="inline-block mr-1" /> View More
          </button>
        ),
      },
    ],
    [memberType]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredStudents,
      initialState: { pageIndex: 0 },
    },
    usePagination
  );

  const getRowClass = (student) => {
    let base = "transition duration-200";

    const fud = student.follow_up_date ? new Date(student.follow_up_date) : null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let isPastOrToday = false;
    if (fud) {
      fud.setHours(0, 0, 0, 0);
      isPastOrToday = fud.getTime() <= today.getTime();
    }
    
    const isLatest = student.student_id === latestFollowUpId;

    if (isLatest) {
      base += " bg-yellow-200 border-4 border-yellow-600 shadow-xl font-semibold";
    } else if (isPastOrToday && !isLatest) {
      base += " bg-green-100 border-2 border-green-500 font-semibold";
    } else {
      base += " hover:bg-gray-100";
    }

    return base;
  };
useEffect(() => {
  if (!shouldAutoScroll || !latestFollowUpId || !tableRef.current) return;

  const scrollAndHighlightLatest = () => {
    if (!tableRef.current) return; // extra safety
    const row = tableRef.current.querySelector(`tr[data-student-id="${latestFollowUpId}"]`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      setShouldAutoScroll(false);
    }
  };

  const studentIndex = filteredStudents.findIndex(s => s.student_id === latestFollowUpId);
  if (studentIndex === -1) {
    setShouldAutoScroll(false);
    return;
  }

  const targetPage = Math.floor(studentIndex / pageSize);

  if (pageIndex !== targetPage) {
    gotoPage(targetPage);
    setTimeout(scrollAndHighlightLatest, 600);
  } else {
    setTimeout(scrollAndHighlightLatest, 200);
  }
}, [shouldAutoScroll, latestFollowUpId, filteredStudents, pageIndex, pageSize, gotoPage]);
  const clearFilters = () => {
    setSelectedRange('');
    setFromDate(null);
    setToDate(null);
    setIsCustomRange(false);
    setSearchQuery('');
    setSelectedFilter('All');
    setSearchDistrict('');
    setSearchCity('');
    setSearchCollege([]);
    setFilteredStudents(studentsData);
  };

  return (
    <div className="mx-auto">
      {loading ? (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
          <span className="text-white text-lg">Fetching Data...</span>
        </div>
      ) : (
        <>
          <div className="mx-auto">
            {loadingSubmit && (
              <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
                <span className="text-white text-lg">Submitting...</span>
              </div>
            )}

            <div className='flex justify-between mb-6'>
              <h1 className="text-3xl font-bold text-center">Not Interested Students</h1>
              <div>
<button
  onClick={() => setSearchModalIsOpen(true)}
  className="bg-blue-500 text-white px-4 py-2 rounded-lg ml-4 flex items-center"
>
  Advanced Search
</button>
                <button
                  className="bg-green-500 text-white px-4 py-2 rounded-lg ml-4 mt-2"
                  onClick={downloadExcelReport}
                >
                  Download Excel Report
                </button>
              </div>
            </div>

            <div className='flex justify-between'>
              <div>
                <button
                  className={`py-2 px-4 rounded-lg text-white ${selectedFilter === 'All' ? 'bg-gray-500 font-semibold' : 'bg-blue-500'}`}
                  onClick={handleAllClick}
                >
                  All
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-white ml-6 ${selectedFilter === 'JEE Mains' ? 'bg-gray-500 font-semibold' : 'bg-blue-500'}`}
                  onClick={() => handleFilterChange('JEE Mains')}
                >
                  JEE Mains-1
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-white ml-6 ${selectedFilter === 'JEE Mains2' ? 'bg-gray-500 font-semibold' : 'bg-blue-500'}`}
                  onClick={() => handleFilterChange('JEE Mains2')}
                >
                  JEE Mains-2
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-white ml-6 ${selectedFilter === 'Not JEE Mains' ? 'bg-gray-500 font-semibold' : 'bg-blue-500'}`}
                  onClick={() => handleFilterChange('Not JEE Mains')}
                >
                  Non-JEE Mains
                </button>
              </div>

              {['Admin', 'Manager'].includes(memberType) && (
                <div>
                  <label htmlFor="dateRange">Select Date Range:</label>
                  <select
                    id="dateRange"
                    className='bg-blue-500 py-2 px-4 text-white rounded-lg ml-4'
                    onChange={handleRangeChange}
                    value={selectedRange}
                  >
                    <option value="">Select a range</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="thisWeek">This Week</option>
                    <option value="lastWeek">Last Week</option>
                    <option value="custom">Custom</option>
                  </select>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 ml-4 bg-red-500 text-white rounded-lg hover:bg-red-600 focus:outline-none"
                  >
                    Clear Filters
                  </button>

                  {isCustomRange && (
                    <div className='flex mt-4 gap-4'>
                      <div>
                        <label>From:</label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={minEnrolledDate}
                          maxDate={toDate || maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select From Date"
                          className='p-2 ml-2 border rounded-lg'
                        />
                      </div>
                      <div>
                        <label>To:</label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate || minEnrolledDate}
                          maxDate={maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select To Date"
                          className='p-2 ml-2 border rounded-lg'
                          disabled={!fromDate}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className='w-1/4'>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={['Admin', 'Manager'].includes(memberType)
                    ? "Search by student name or mobile number or employee name"
                    : "Search by student name or mobile number"}
                  className="py-2 px-4 border rounded-lg w-full"
                />
              </div>
            </div>

            <div className="overflow-x-auto p-4">
              <div ref={tableRef}>
                <table {...getTableProps()} key={filteredStudents.length} className="min-w-full mb-4 rounded-lg shadow-md overflow-hidden">
                  <thead className="bg-gray-200">
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                          <th {...column.getHeaderProps()} className="px-4 py-2 text-left text-gray-700 font-semibold">
                            {column.render('Header')}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                      prepareRow(row);
                      const student = row.original;
                      const studentId = student.student_id;
                      const rowClass = getRowClass(student);

                      return (
                        <tr
                          {...row.getRowProps()}
                          data-student-id={studentId}
                          className={rowClass}
                        >
                          {row.cells.map(cell => (
                            <td {...cell.getCellProps()} className="border px-4 py-2">
                              {cell.render('Cell')}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center mb-4">
                <span>
                  Page {pageIndex + 1} of {pageCount} of {filteredStudents.length} entries
                </span>
                <div>
                  <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-3 py-1 border rounded">{"<<"}</button>
                  <button onClick={previousPage} disabled={!canPreviousPage} className="px-3 py-1 border rounded">{"<"}</button>
                  <button onClick={nextPage} disabled={!canNextPage} className="px-3 py-1 border rounded">{">"}</button>
                  <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="px-3 py-1 border rounded">{">>"}</button>
                </div>
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="ml-4 border rounded">
                  {[5, 10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <NotInterestedModal
              isOpen={modalIsOpen}
              onRequestClose={closeModal}
              selectedStudent={selectedStudent}
              isUpdate={isUpdate}
              isUpload={isUpload}
              formData={formData}
              setFormData={setFormData}
              selectedCourses={selectedCourses}
              setSelectedCourses={setSelectedCourses}
              dropdownValue={dropdownValue}
              setDropdownValue={setDropdownValue}
              canOnlySelectHigherInterest={canOnlySelectHigherInterest}
              loadingSubmit={loadingSubmit}
              handleChange={handleChange}
              handleSelectChange={handleSelectChange}
              handleRemoveTag={handleRemoveTag}
              handleSubmit={handleSubmit}
              memberType={memberType}
              courses={courses}
              districts={districts}
            />

<AdvancedSearchModal
  isOpen={searchModalIsOpen}
  onRequestClose={() => setSearchModalIsOpen(false)}
  filteredStudents={filteredStudents}
  searchDistrict={searchDistrict}
  setSearchDistrict={setSearchDistrict}
  searchCity={searchCity}
  setSearchCity={setSearchCity}
  searchCollege={searchCollege}
  setSearchCollege={setSearchCollege}
  setFilteredStudents={setFilteredStudents}
  studentsData={studentsData}
/>
          </div>
        </>
      )}
    </div>
  );
};

export default NotInterestedStudents;
